import React, { useState, useEffect } from 'react';
import { connect } from 'redux-zero/react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';

import { formActions } from 'app/state/actions';
import { BillboardService, HolderService } from 'services';
import {
  DsButton,
  DsForm,
  DsIcon,
  DsInputBignumber,
  DsInputString,
  DsRadioToggle,
  DsSelect,
  DsSelectOption,
  DsSelectSimple,
  DsTag,
} from '@tokenyico/design-system/dist/reactJS';
import {
  vMinLength,
  vBigNumber,
  vGreaterThanBigNumber,
  vEmail,
  vPattern,
  vMaxBigNumber,
  vRequired,
  vWalletAddress,
} from '@tokenyico/design-system';
import { toChecksumAddress } from 'app/helpers/investor';

import './styles.less';

const valuesInitialState = {
  buyingToken: { tokenId: null, amount: null },
  sellingToken: { tokenId: null, amount: null },
  contactInformation: {
    emailAddress: null,
    walletAddress: null,
    phoneNumber: null,
    other: null,
    otherType: null,
    otherValue: null,
  },
  isAnonymous: false,
  title: 'null',
  shortDescription: 'null',
  longDescription: 'null',
  offerImage: null,
  urgencyLevel: 'NOT_URGENT',
  tokenId: null,
};

const BillboardCreateOfferView = ({
  onCloseModal,
  setError,
  setSuccess,
  holder: {
    selectedToken,
    user: { walletsAndStatus },
  },
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tradableTokens, setTradableTokens] = useState([]);
  const [stableCoins, setStableCoins] = useState([]);
  const [selectedSellingToken, setSelectedSellingToken] = useState(null);
  const [selectedBuyingToken, setSelectedBuyingToken] = useState(null);
  const [isSellingTokenSC, setIsSellingTokenSC] = useState(false);
  const [balance, setBalance] = useState(null);
  const [values, setValues] = useState(valuesInitialState);
  const [createBuyOrSellOffer, setCreateBuyOrSellOffer] = useState('buy');

  const toggleLabels = [t('shared.buy'), t('shared.sell')];

  const toggleValues = ['buy', 'sell'];

  useAsync(async () => {
    try {
      const dataSettings = await BillboardService.getOfferSettings(selectedToken.id);
      setTradableTokens([selectedToken]);
      setStableCoins(dataSettings?.tradableTo ?? []);

      const dataBalance = await HolderService.getBalances(selectedToken.id);
      setBalance(
        parseInt(dataBalance[0]?.unfrozenBalance) ? dataBalance[0]?.unfrozenBalance : 0 ?? 0,
      ); //show decimal 0.00 balance as integer 0. Undefined balance will be 0 as well.
    } catch (e) {
      setError(e);
    }
  });

  useEffect(() => {
    allOptions();
    buyTokenOptions();
    sellTokenOptions();
    setIsSellingTokenSC(!!stableCoins.filter((sc) => sc.id === values.sellingToken.tokenId).length);
    setSelectedSellingToken(sellTokenOptions().find((t) => t.id === values.sellingToken?.tokenId));
    setSelectedBuyingToken(buyTokenOptions().find((t) => t.id === values.buyingToken?.tokenId));
  }, [buyTokenOptions(), sellTokenOptions(), values]);

  function allOptions() {
    return tradableTokens.concat(stableCoins);
  }

  function buyTokenOptions() {
    if (createBuyOrSellOffer === 'buy') {
      return tradableTokens;
    } else {
      return stableCoins;
    }
  }

  function sellTokenOptions() {
    if (createBuyOrSellOffer === 'buy') {
      return stableCoins;
    } else {
      return tradableTokens;
    }
  }

  const buyToken = () =>
    values.buyingToken.tokenId
      ? allOptions().find((token) => token.id === values.buyingToken.tokenId)
      : {};

  const sellToken = () =>
    values.sellingToken.tokenId
      ? allOptions().find((token) => token.id === values.sellingToken.tokenId)
      : {};

  const onSubmit = async () => {
    setIsSubmitting(true);

    try {
      const data = {
        sellingToken: {
          tokenId: values.sellingToken.tokenId,
          amount: values.sellingToken.amount.toString(),
        },
        buyingToken: {
          tokenId: values.buyingToken.tokenId,
          amount: values.buyingToken.amount.toString(),
        },
        isAnonymous: false,
        urgencyLevel: 'NOT_URGENT',
        title: 'null',
        shortDescription: 'null',
        longDescription: 'null',
        tokenId: selectedToken.id,
        contactInformation: {
          emailAddress: values.contactInformation.emailAddress,
          walletAddress: values.contactInformation.walletAddress,
          phoneNumber: values.contactInformation.phoneNumber,
          other: values.contactInformation.otherValue
            ? `${values.contactInformation.otherType}: ${values.contactInformation.otherValue}`
            : null,
        },
      };
      await BillboardService.createOffer(data);
      setSuccess(t('shared.offerCreated'));
      handleCloseModal(true);
    } catch (e) {
      setError(e);
    }

    setIsSubmitting(false);
  };

  const handleCloseModal = (success) => {
    setValues(valuesInitialState);
    onCloseModal(success);
  };

  return (
    <>
      <h3 className="offer-header h3">{t('shared.createOffer')}</h3>
      <div className="buy-sell-toggle">
        <DsRadioToggle
          labels={toggleLabels}
          name="createBuyOrSellOffer"
          values={toggleValues}
          value={createBuyOrSellOffer}
          onDsInput={({ detail }) => setCreateBuyOrSellOffer(detail)}
          isFullWidth
          isMini={false}
        />
      </div>
      <DsForm onSubmit={onSubmit} className="create-offer__form">
        <div className="offer-section">
          <div className="offer-container">
            <span className="_text:sm _flex-align-center _mbottom-1">
              <DsIcon name="sending" size="14px" color="var(--coolGrey-700)" class="_mright-2" />
              <b>
                {createBuyOrSellOffer === 'buy' ? t('shared.iWillPay') : t('shared.iWillSell')} *
              </b>
            </span>
            <div className="offer">
              <div className="offer__quantity">
                <DsInputBignumber
                  name="quantityToSell"
                  value={values.sellingToken.amount}
                  onDsInput={({ detail }) =>
                    setValues({
                      ...values,
                      sellingToken: { ...values.sellingToken, amount: detail },
                    })
                  }
                  validate={(v) =>
                    vBigNumber(sellToken()?.decimals || 0)(v) ||
                    vGreaterThanBigNumber('0')(v) ||
                    (!isSellingTokenSC && values.sellingToken.tokenId && vMaxBigNumber(balance)(v)
                      ? t('validation.maxBalance')
                      : null)
                  }
                  placeholder={(0).toFixed(sellToken()?.decimals || 0)}
                />
              </div>
              <div className="offer__token">
                <DsSelect
                  name="tokenToSell"
                  value={values.sellingToken.tokenId}
                  onDsInput={({ detail }) =>
                    setValues({
                      ...values,
                      sellingToken: { ...values.sellingToken, tokenId: detail },
                    })
                  }
                  validate={vRequired}
                  isClearable
                >
                  <div slot="value">
                    <div className="_flex:row _flex:align(center)">
                      <img
                        src={selectedSellingToken?.image || selectedSellingToken?.logo}
                        className="_m:right(2)"
                      />
                      <span>{selectedSellingToken?.symbol}</span>
                    </div>
                  </div>
                  <div slot="options">
                    {sellTokenOptions().map((sellTokenOption) => (
                      <DsSelectOption value={sellTokenOption.id} key={sellTokenOption.id}>
                        <div className="_flex:row _flex:align(center)">
                          <img
                            src={sellTokenOption.image || sellTokenOption.logo}
                            name="sellTokenLogo"
                            className="_m:right(2)"
                          />
                          <span>{sellTokenOption.symbol}</span>
                        </div>
                      </DsSelectOption>
                    ))}
                  </div>
                </DsSelect>
              </div>
            </div>
            {values.sellingToken.tokenId && createBuyOrSellOffer === 'sell' && (
              <div className="_text:xs _text-coolGrey-500 _text(500)">
                <span className="_text-coolGrey-400 _text(400)">Balance:&nbsp;</span>
                {balance} {selectedSellingToken?.symbol}
              </div>
            )}
          </div>

          <div className="transfer-icon _shadow-2">
            <DsIcon name="transfer" size="16px" color="white" />
          </div>

          <div className="offer-container">
            <span className="_text:sm _flex-align-center _mbottom-1">
              <DsIcon name="receiving" size="14px" color="var(--coolGrey-700)" class="_mright-2" />
              <b>
                {createBuyOrSellOffer === 'buy' ? t('shared.iWillBuy') : t('shared.iWillReceive')} *
              </b>
            </span>

            <div className="offer">
              <div className="offer__quantity">
                <DsInputBignumber
                  name="quantityToBuy"
                  value={values.buyingToken.amount}
                  onDsInput={({ detail }) =>
                    setValues({ ...values, buyingToken: { ...values.buyingToken, amount: detail } })
                  }
                  validate={(v) =>
                    vBigNumber(buyToken()?.decimals || 0)(v) || vGreaterThanBigNumber('0')(v)
                  }
                  placeholder={(0).toFixed(buyToken()?.decimals || 0)}
                  isClearable
                />
              </div>
              <div className="offer__token">
                <DsSelect
                  name="tokenToBuy"
                  value={values.buyingToken.tokenId}
                  onDsInput={({ detail }) =>
                    setValues({
                      ...values,
                      buyingToken: { ...values.buyingToken, tokenId: detail },
                    })
                  }
                  validate={vRequired}
                  isClearable
                >
                  <div slot="value">
                    <div className="_flex:row _flex:align(center)">
                      <img
                        src={selectedBuyingToken?.image || selectedBuyingToken?.logo}
                        className="_m:right(2)"
                      />
                      <span>{selectedBuyingToken?.symbol}</span>
                    </div>
                  </div>
                  <div slot="options">
                    {buyTokenOptions().map((buyTokenOption) => (
                      <DsSelectOption value={buyTokenOption.id} key={buyTokenOption.id}>
                        <div className="_flex:row _flex:align(center)">
                          <img
                            src={buyTokenOption.image || buyTokenOption.logo}
                            name="buyTokenLogo"
                            className="_m:right(2)"
                          />
                          <span>{buyTokenOption.symbol}</span>
                        </div>
                      </DsSelectOption>
                    ))}
                  </div>
                </DsSelect>
              </div>
            </div>
          </div>
        </div>
        <div className="contact-section">
          <div className="_col(12) _m:bottom(3)">
            <h2 className="_text:sm _text(500)">{t('shared.contactInformation')}</h2>
            <p className="_text:xs _text-coolGrey-400 _m:top(1)">
              {t('shared.contactInformationDescription')}
            </p>
          </div>

          <div className="_col(12)">
            <DsInputString
              name="email"
              value={values.contactInformation.emailAddress ?? ''}
              label={t('shared.email') + ' *'}
              onDsInput={({ detail }) => {
                setValues({
                  ...values,
                  contactInformation: {
                    ...values.contactInformation,
                    emailAddress: detail,
                  },
                });
              }}
              validate={(v) => vEmail(v)}
            />
          </div>
          <div className="_col(12)">
            {isSellingTokenSC ? (
              <DsSelectSimple
                value={values.contactInformation.walletAddress}
                onDsInput={({ detail }) =>
                  setValues({
                    ...values,
                    contactInformation: {
                      ...values.contactInformation,
                      walletAddress: detail,
                    },
                  })
                }
                name="wallets"
                label={t('shared.wallet')}
                options={walletsAndStatus
                  .filter((w) => w.status === 'Qualified')
                  .map((item) => ({
                    value: toChecksumAddress(item.wallet),
                    label: toChecksumAddress(item.wallet),
                  }))}
                isClearable
              />
            ) : (
              <DsInputString
                name="wallet"
                value={values.contactInformation.walletAddress}
                label={t('shared.wallet')}
                onDsInput={({ detail }) => {
                  setValues({
                    ...values,
                    contactInformation: {
                      ...values.contactInformation,
                      walletAddress: detail || null,
                    },
                  });
                }}
                validate={vWalletAddress}
              />
            )}

            <DsTag
              label={t('shared.contactWalletInfos')}
              version="info"
              icon="info"
              className="_m:top(2)"
            />
          </div>
          <div className="_col(12) _col:md(6)">
            <DsInputString
              label={t('shared.phoneNumber')}
              name="phone"
              value={values.contactInformation.phoneNumber}
              onDsInput={({ detail }) =>
                setValues({
                  ...values,
                  contactInformation: { ...values.contactInformation, phoneNumber: detail || null },
                })
              }
              validate={(v) =>
                values.contactInformation.phoneNumber &&
                vPattern(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/)(v)
                  ? t('validation.phoneNumber')
                  : undefined
              }
            />
          </div>
          <div className="_col(12) _col:md(6)">
            <DsSelectSimple
              value={values.contactInformation.otherType ?? null}
              onDsInput={({ detail }) =>
                setValues({
                  ...values,
                  contactInformation: {
                    ...values.contactInformation,
                    otherType: detail,
                  },
                })
              }
              name="otherValueType"
              label={t('shared.other')}
              options={[
                {
                  value: 'Whatsapp',
                  label: 'Whatsapp',
                },
                {
                  value: 'telegram',
                  label: 'Telegram',
                },
                {
                  value: 'discord',
                  label: 'Discord',
                },
              ]}
              isClearable
            />
          </div>
          {values.contactInformation.otherType && (
            <div className="_col(12)">
              <DsInputString
                label={t('shared.otherValue')}
                name="otherValue"
                value={values.contactInformation.otherValue ?? ''}
                onDsInput={({ detail }) =>
                  setValues({
                    ...values,
                    contactInformation: {
                      ...values.contactInformation,
                      otherValue: detail,
                    },
                  })
                }
                isClearable
                validate={(v) => values.contactInformation.otherValue && vMinLength(1)(v)}
              />
            </div>
          )}
          <div className="_col(12) _m:top(4) _flex:justify(end)">
            <DsButton
              version="monochrome"
              label={t('shared.discard')}
              type="button"
              onClick={() => handleCloseModal(false)}
            />
            <DsButton
              version="primary"
              label={t('shared.create')}
              type="submit"
              isLoading={isSubmitting}
              className="_m:left(2)"
            />
          </div>
        </div>
      </DsForm>
    </>
  );
};

const mapToProps = (state) => state;

export default connect(mapToProps, formActions)(BillboardCreateOfferView);
